import { StandardizedBalanceData, BalanceValues, BalanceTotals } from '../types/interfaces';

/**
 * Convierte datos del balance sheet a la estructura estandarizada { values, totals }
 */
export const standardizeBalanceData = (balanceData: any): StandardizedBalanceData => {
  const formatEuro = (value: number) => {
    // Redondear explícitamente a 2 decimales para evitar errores de precisión
    const rounded = Math.round(value * 100) / 100;
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(rounded);
  };

  const sumValues = (obj: any) =>
    Object.values(obj).reduce((sum: number, val: any) => sum + parseFloat(val || 0), 0);

  const safeParseFloat = (value: any, defaultValue: number = 0) => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  // Calcular totales con redondeo en cada paso para evitar acumulación de errores
  const gross_result = Math.round((
    safeParseFloat(balanceData.gross_result.agricultural_production) -
    safeParseFloat(balanceData.gross_result.production_cost) +
    safeParseFloat(balanceData.gross_result.sales) -
    safeParseFloat(balanceData.gross_result.cost_of_sales)
  ) * 100) / 100;

  const operating_result = Math.round((
    gross_result -
    safeParseFloat(balanceData.operating_result.commercialization_expenses) -
    safeParseFloat(balanceData.operating_result.administrative_expenses) +
    safeParseFloat(balanceData.operating_result.other_income) -
    safeParseFloat(balanceData.operating_result.other_expenses)
  ) * 100) / 100;

  const ordinary_result = Math.round((
    operating_result -
    safeParseFloat(balanceData.ordinary_result.financial_expenses) +
    safeParseFloat(balanceData.ordinary_result.exchange_difference_and_holding_result)
  ) * 100) / 100;

  const result_before_taxes = Math.round((
    ordinary_result +
    safeParseFloat(balanceData.result_before_taxes.extraordinary_income) -
    safeParseFloat(balanceData.result_before_taxes.extraordinary_expenses)
  ) * 100) / 100;

  // Usar resultForTheYear del backend si está disponible, sino calcularlo restando el impuesto
  // El cálculo debe ser: result_before_taxes - income_tax (sin valor absoluto, igual que el backend)
  const result_for_the_year = balanceData.result_for_the_year !== undefined && balanceData.result_for_the_year !== null
    ? safeParseFloat(balanceData.result_for_the_year)
    : Math.round((
        result_before_taxes + safeParseFloat(balanceData.taxes.income_tax)
      ) * 100) / 100;

  const total_current =
    (balanceData.current_assets ? sumValues(balanceData.current_assets) : 0) +
    (balanceData.non_current_assets ? sumValues(balanceData.non_current_assets) : 0);
  
  const total_non_current =
    (balanceData.current_liabilities ? sumValues(balanceData.current_liabilities) : 0) +
    (balanceData.non_current_liabilities ? sumValues(balanceData.non_current_liabilities) : 0);
  
  const total_net_worth = balanceData.net_worth ? sumValues(balanceData.net_worth) : 0;
  
  const total_net_worth_and_non_current =
    (balanceData.current_liabilities ? sumValues(balanceData.current_liabilities) : 0) +
    (balanceData.non_current_liabilities ? sumValues(balanceData.non_current_liabilities) : 0) +
    (balanceData.net_worth ? sumValues(balanceData.net_worth) : 0) +
    (balanceData.non_controlling_interest_result ? sumValues(balanceData.non_controlling_interest_result) : 0);

  // Crear estructura de valores (convertir strings a numbers)
  const values: BalanceValues = {
    id: balanceData.id,
    cuit: balanceData.cuit,
    code: balanceData.code,
    from_month: balanceData.from_month,
    from_year: balanceData.from_year,
    to_month: balanceData.to_month,
    to_year: balanceData.to_year,
    current_assets: {
      current_investments: safeParseFloat(balanceData.current_assets?.current_investments),
      cash_and_cash_equivalents: safeParseFloat(balanceData.current_assets?.cash_and_cash_equivalents),
      trade_receivables_short_term: safeParseFloat(balanceData.current_assets?.trade_receivables_short_term),
      other_receivables: safeParseFloat(balanceData.current_assets?.other_receivables),
      inventory_short_term: safeParseFloat(balanceData.current_assets?.inventory_short_term),
      other_current_assets: safeParseFloat(balanceData.current_assets?.other_current_assets),
    },
    non_current_assets: {
      trade_receivables_long_term: safeParseFloat(balanceData.non_current_assets?.trade_receivables_long_term),
      other_receivables: safeParseFloat(balanceData.non_current_assets?.other_receivables),
      permanent_investments: safeParseFloat(balanceData.non_current_assets?.permanent_investments),
      property_plant_and_equipment: safeParseFloat(balanceData.non_current_assets?.property_plant_and_equipment),
      other_non_current_assets: safeParseFloat(balanceData.non_current_assets?.other_non_current_assets),
    },
    current_liabilities: {
      trade_payables_short_term: safeParseFloat(balanceData.current_liabilities?.trade_payables_short_term),
      bank_and_financial_debt_short_term: safeParseFloat(balanceData.current_liabilities?.bank_and_financial_debt_short_term),
      social_security_debt_short_term: safeParseFloat(balanceData.current_liabilities?.social_security_debt_short_term),
      tax_liabilities_short_term: safeParseFloat(balanceData.current_liabilities?.tax_liabilities_short_term),
      other_debts_provisions: safeParseFloat(balanceData.current_liabilities?.other_debts_provisions),
      projections: safeParseFloat(balanceData.current_liabilities?.projections),
      other_liabilities: safeParseFloat(balanceData.current_liabilities?.other_liabilities),
    },
    non_current_liabilities: {
      non_current_liabilities: safeParseFloat(balanceData.non_current_liabilities?.non_current_liabilities),
      trade_payables_long_term: safeParseFloat(balanceData.non_current_liabilities?.trade_payables_long_term),
      bank_and_financial_debt_long_term: safeParseFloat(balanceData.non_current_liabilities?.bank_and_financial_debt_long_term),
      social_security_debt_long_term: safeParseFloat(balanceData.non_current_liabilities?.social_security_debt_long_term),
      tax_liabilities_long_term: safeParseFloat(balanceData.non_current_liabilities?.tax_liabilities_long_term),
      other_long_term_liabilities: safeParseFloat(balanceData.non_current_liabilities?.other_long_term_liabilities),
    },
    non_controlling_interest_result: {
      non_controlling_interest_result: safeParseFloat(balanceData.non_controlling_interest_result?.non_controlling_interest_result),
    },
    net_worth: {
      capital: safeParseFloat(balanceData.net_worth?.capital),
      other_components: safeParseFloat(balanceData.net_worth?.other_components),
    },
    gross_result: {
      agricultural_production: safeParseFloat(balanceData.gross_result?.agricultural_production),
      production_cost: safeParseFloat(balanceData.gross_result?.production_cost),
      sales: safeParseFloat(balanceData.gross_result?.sales),
      cost_of_sales: safeParseFloat(balanceData.gross_result?.cost_of_sales),
    },
    operating_result: {
      commercialization_expenses: safeParseFloat(balanceData.operating_result?.commercialization_expenses),
      administrative_expenses: safeParseFloat(balanceData.operating_result?.administrative_expenses),
      other_income: safeParseFloat(balanceData.operating_result?.other_income),
      other_expenses: safeParseFloat(balanceData.operating_result?.other_expenses),
    },
    ordinary_result: {
      financial_expenses: safeParseFloat(balanceData.ordinary_result?.financial_expenses),
      exchange_difference_and_holding_result: safeParseFloat(balanceData.ordinary_result?.exchange_difference_and_holding_result),
    },
    result_before_taxes: {
      extraordinary_income: safeParseFloat(balanceData.result_before_taxes?.extraordinary_income),
      extraordinary_expenses: safeParseFloat(balanceData.result_before_taxes?.extraordinary_expenses),
    },
    taxes: {
      income_tax: safeParseFloat(balanceData.taxes?.income_tax),
    },
  };

  // Crear estructura de totales (formateados como strings)
  const totals: BalanceTotals = {
    total_current: formatEuro(total_current),
    total_non_current: formatEuro(total_non_current),
    total_net_worth: formatEuro(total_net_worth),
    total_net_worth_and_non_current: formatEuro(total_net_worth_and_non_current),
    gross_result: formatEuro(gross_result),
    operating_result: formatEuro(operating_result),
    ordinary_result: formatEuro(ordinary_result),
    result_before_taxes: {
      extraordinary_income: formatEuro(safeParseFloat(balanceData.result_before_taxes?.extraordinary_income)),
      extraordinary_expenses: formatEuro(safeParseFloat(balanceData.result_before_taxes?.extraordinary_expenses)),
      total: result_before_taxes
    },
    result_before_taxes_calculated: result_before_taxes,
    taxes: formatEuro(result_for_the_year),
  };

  return {
    values,
    totals
  };
};
